import './MenuContainer.scss';

import {
  Button,
  ButtonStyle,
  Header,
  HeaderVariant,
  IconSize,
  Icons,
  ModalVariant,
  Portal,
  SearchBar,
  TitleSize,
} from '@pdcfrontendui/components';
import {
  FavoriteStatusEnum,
  NotificationStatusEnum,
} from '@pdcplan/shared/enums';
import { Period, toDefaultLoadPeriod } from '../util/dates';
import {
  attemptLoadTeamRoster,
  attemptLoadTeamRosterMulti,
} from '../ListView/ListViewActions';
import {
  attemptSetFavoriteStatus,
  attemptSetNotificationStatus,
} from './MenuActions';
import {
  atteptGetUnreadMessagesCount,
  showConfirmDialog,
  showInfoDialog,
} from '../appActions';
import { deviceType, primaryInput } from 'detect-it';
import { useBool, useIsDualView } from '@pdcfrontendui/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MenuItemLoadedType } from './MenuReducer';
import MenuLists from './MenuLists';
import PersonelgroupInfoModal from './PersonelgroupInfoModal';
import { Store } from '../rootReducer';
import { TeamMap } from '../api/model';
import { ThunkDispatch } from '../types';
import classNames from 'classnames';
import colorVarMap from '@pdcfrontendui/components/colors/colors';
import { connect } from 'react-redux';
import { currentLanguage } from '../currentLanguage';
import { getSiteRoutes } from '../routes';
import ids from '../testing/ids';
import { searchString } from '@pdcfrontendui/utils';
import { tooManyGroupMembers } from './menuUtils';
import { useNavigate } from 'react-router-dom';
import { DayMessageMap } from '../util/getChatKey';
import { countUnreadMessages } from '../Chat/chatUtils';

const scss = {
  Comp: 'MenuContainer',
  shown: 'shown',
  searchContainer: 'searchContainer',
  searchInput: 'searchInput',
  headerLeftIcons: 'headerLeftIcons',
  NewMenuInfoModalBody: 'NewMenuInfoModalBody',
  isDualView: 'isDualView',
  overlayBackground: 'overlayBackground',
  noPersonelGroups: 'noPersonelGroups',
};

type Props = {
  shouldShow: boolean;
  currentTeam: string;
  currentDate: Date | null;
  onClose: () => void;
};

export type StateFromProps = {
  teams: TeamMap;
  menuItemLoading: MenuItemLoadedType;
  unreadCountMap: Record<string, number>;
  actionsRequired: Record<string, number>;
  loadingTeams: boolean;
  activeScope: number;
  dayMessageMap: DayMessageMap;
  userId: number;
  period: Period;
};

export type DispatchFromProps = {
  loadTeamRoster: (teamId: string, requestPeriod: Period) => void;
  loadTeamRosterAll: (teamIds: string[], requestPeriod: Period) => void;
  checkUnreadMessageCount: (teamIds: string[], from: Date, to: Date) => void;
  setFavoriteStatus: (
    teamId: string,
    teamLabel: string,
    status: FavoriteStatusEnum
  ) => void;
  setNotificationStatus: (
    teamId: string,
    status: NotificationStatusEnum
  ) => void;
  showTooManyMembersDialog: () => void;
};

const mapStateToProps = (store: Store): StateFromProps => ({
  teams: store.chooseTeamReducer.teams,
  loadingTeams: store.chooseTeamReducer.loading,
  menuItemLoading: store.menuReducer.menuItemLoading,
  unreadCountMap: store.appReducer.unreadCountMap,
  actionsRequired: store.listViewReducer.actionRequiredCounters,
  activeScope: store.appReducer.settings.activeScope,
  dayMessageMap: store.appReducer.dayMessageMap,
  userId: store.loginReducer.userId,
  period: store.listViewReducer.period,
});

const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchFromProps => ({
  loadTeamRoster: (teamId, requestPeriod) => {
    void dispatch(attemptLoadTeamRoster(teamId, requestPeriod, false, false));
  },
  loadTeamRosterAll: (teamIds, requestPeriod) => {
    void dispatch(attemptLoadTeamRosterMulti(teamIds, requestPeriod));
  },
  checkUnreadMessageCount: (teamIds, from, to) => {
    void dispatch(atteptGetUnreadMessagesCount(teamIds, from, to, false));
  },
  setFavoriteStatus: (teamId, teamLabel, status) => {
    if (status === FavoriteStatusEnum.nothing) {
      dispatch(
        showConfirmDialog({
          title: currentLanguage.UnfollowDialogTitle,
          message: currentLanguage.UnfollowDialogBody_1(teamLabel),
          buttonOkText: currentLanguage.UnfollowGroup,
          buttonCancelText: currentLanguage.Cancel,
          variant: ModalVariant.Alert,
          onOk: () => {
            void dispatch(attemptSetFavoriteStatus(teamId, status));
          },
        })
      );
    } else {
      void dispatch(attemptSetFavoriteStatus(teamId, status));
    }
  },
  setNotificationStatus: (teamId, status) => {
    void dispatch(attemptSetNotificationStatus(teamId, status));
  },
  showTooManyMembersDialog: () => {
    dispatch(
      showInfoDialog({
        title: currentLanguage.TooManyGroupsAlertTitle,
        message: currentLanguage.TooManyGroupsAlertBody,
        onOk: () => {},
        buttonText: currentLanguage.Close,
        buttonStyle: ButtonStyle.Secondary,
        variant: ModalVariant.Alert,
      })
    );
  },
});

export const MenuContainer = ({
  teams,
  currentTeam,
  currentDate,
  shouldShow,
  menuItemLoading,
  unreadCountMap: _unreadCountMap,
  actionsRequired,
  loadingTeams,
  activeScope,
  loadTeamRoster,
  loadTeamRosterAll,
  setFavoriteStatus,
  setNotificationStatus,
  checkUnreadMessageCount,
  showTooManyMembersDialog,
  onClose,
  dayMessageMap,
  userId,
  period,
}: Props & StateFromProps & DispatchFromProps) => {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isInfoShown, showInfo, hideInfo] = useBool(false);
  const isDualView = useIsDualView();
  const useTouch = deviceType === 'touchOnly' || primaryInput === 'touch';

  const teamList = useMemo(() => Object.values(teams), [teams]);
  const hasCheckedTeamStatuses = useRef(false);
  const unreadCountMap: Record<string, number> = useMemo(
    () => ({
      ..._unreadCountMap,
      // Counter for the current team might not correspond to what the backend returns,
      // since we only mark as read in the frontend when changing team.
      [currentTeam]: countUnreadMessages(
        dayMessageMap,
        userId,
        period.from,
        period.to
      ),
    }),
    [
      dayMessageMap,
      _unreadCountMap,
      currentTeam,
      userId,
      period.from,
      period.to,
    ]
  );

  const loadTeamData = useCallback(
    (ids: string[]) => {
      if (currentDate) {
        const period = toDefaultLoadPeriod(currentDate, activeScope);

        if (ids.length === 1) {
          loadTeamRoster(ids[0]!, period);
        } else {
          loadTeamRosterAll(ids, period);
        }

        checkUnreadMessageCount(ids, period.from, period.to);
      }
    },
    [checkUnreadMessageCount, currentDate, loadTeamRoster, loadTeamRosterAll]
  );

  useEffect(() => {
    // Should only be called once pr. session
    if (teamList.length && !hasCheckedTeamStatuses.current) {
      const unfollowedTeams = teamList.filter(
        (team) => team.favoriteStatus === FavoriteStatusEnum.nothing
      );
      if (
        teamList.length === unfollowedTeams.length &&
        !tooManyGroupMembers(
          unfollowedTeams.map((team) => team.groupMemberCount)
        )
      ) {
        unfollowedTeams.forEach((team) => {
          setFavoriteStatus(team.id, team.label, FavoriteStatusEnum.favorite);
        });

        if (unfollowedTeams.length > 0) {
          loadTeamData(unfollowedTeams.map((team) => team.id));
        }
      } else {
        loadTeamData(
          teamList
            .filter(
              (team) => team.favoriteStatus === FavoriteStatusEnum.favorite
            )
            .map((team) => team.id)
        );
      }
      hasCheckedTeamStatuses.current = true;
    }
  }, [teamList, loadTeamData, setFavoriteStatus]);

  const onItemSelect = (id: string) => {
    // Loading shifts for the newly selected team is handled in IntervalPolling.tsx.
    navigate(getSiteRoutes().team(id));
  };

  const sortedAndFilteredTeamIds = useMemo(
    () =>
      teamList
        .filter(
          (team) =>
            searchString(searchText, team.label) ||
            searchString(searchText, team.unitUilabel)
        )
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        ),
    [teamList, searchText]
  );

  return (
    <>
      <Portal>
        <>
          <div
            className={classNames(scss.Comp, {
              [scss.shown]: shouldShow,
              [scss.isDualView]: isDualView,
            })}
            id={ids.Menu.menuComponent}
            role="menu"
          >
            <Header centerize variant={HeaderVariant.Transparent}>
              <Header.Title size={TitleSize.Medium} bold>
                {currentLanguage.ChangePersonelGroup}
              </Header.Title>
              <Header.Left>
                <div className={scss.headerLeftIcons}>
                  <Icons.Info
                    onClick={() => {
                      showInfo();
                    }}
                    color={colorVarMap.colorPrimaryDark}
                  />
                  {!showSearch && (
                    <Icons.Search
                      size={IconSize.XSmall}
                      onClick={() => setShowSearch(true)}
                      color={colorVarMap.colorPrimaryDark}
                      ariaLabel={currentLanguage.search}
                    />
                  )}
                </div>
              </Header.Left>
              <Header.Right>
                <Icons.Clear
                  size={IconSize.Small}
                  onClick={() => onClose()}
                  color={colorVarMap.colorPrimaryDark}
                  ariaLabel={currentLanguage.CloseMenu}
                />
              </Header.Right>
            </Header>
            {showSearch && (
              <div className={scss.searchContainer}>
                <SearchBar
                  className={scss.searchInput}
                  value={searchText}
                  onChange={(value: string) => setSearchText(value)}
                  clearButtonAriaLabel={currentLanguage.clear}
                />
                <Button
                  variant={ButtonStyle.Ghost}
                  onClick={() => {
                    setSearchText('');
                    setShowSearch(false);
                  }}
                >
                  {currentLanguage.Cancel}
                </Button>
              </div>
            )}
            {sortedAndFilteredTeamIds.length > 0 ? (
              <MenuLists
                teams={sortedAndFilteredTeamIds}
                currentTeam={currentTeam}
                actionsRequired={actionsRequired}
                unreadCountMap={unreadCountMap}
                isSearching={showSearch}
                loadingTeams={loadingTeams}
                menuItemLoading={menuItemLoading}
                onItemClick={(teamId) => {
                  onClose();
                  onItemSelect(teamId);
                }}
                onNotificationClick={(teamId, status) => {
                  setNotificationStatus(teamId, status);
                }}
                onFavoriteClick={(team, status) => {
                  const followedTeamsCount = teamList
                    .filter(
                      (team) =>
                        team.favoriteStatus === FavoriteStatusEnum.favorite
                    )
                    .map((team) => team.groupMemberCount);

                  if (
                    status === FavoriteStatusEnum.favorite &&
                    followedTeamsCount.length > 0 &&
                    tooManyGroupMembers([
                      ...followedTeamsCount,
                      team.groupMemberCount,
                    ])
                  ) {
                    showTooManyMembersDialog();
                  } else {
                    setFavoriteStatus(team.id, team.label, status);
                  }
                }}
                onDownloadClick={(teamId) => loadTeamData([teamId])}
                useTouch={useTouch}
              />
            ) : (
              <div className={scss.noPersonelGroups}>
                <div>{currentLanguage.NoOffersInSearch}</div>
              </div>
            )}
          </div>
          {shouldShow && (
            <div
              id={ids.Menu.overlay}
              onClick={() => onClose()}
              className={scss.overlayBackground}
            />
          )}
        </>
      </Portal>
      <PersonelgroupInfoModal
        shown={isInfoShown}
        isTouch={useTouch}
        onHide={hideInfo}
      />
    </>
  );
};

export default connect<StateFromProps, DispatchFromProps, {}, Store>(
  mapStateToProps,
  mapDispatchToProps
)(MenuContainer);
